import React, { FC, Suspense } from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useFormContext, UseFormReturn } from "react-hook-form";
import { flatten } from "utils/objectUtilities";

export type FormFields = FormField[][];

export type FormField<Y extends boolean = true, T = {}> = Y extends true
  ? {
      renderAsFormItem?: Y;
      name: string;
      component?: FC<T>;
      valueTransformer?: <V = unknown>(value: V) => V;
      props?: T | ((form: UseFormReturn | null, parentFieldName?: string) => T);
    }
  : {
      renderAsFormItem: Y;
      component?: FC<T>;
      valueTransformer?: <V = unknown>(value: V) => V;
      props?: T | ((form: UseFormReturn | null, parentFieldName?: string) => T);
    };

export function ff<Y extends boolean, T>(field: FormField<Y, T>) {
  return field;
}

export type FormRendererProps = {
  fields: FormFields;
  parentFieldName?: string;
  errors: {
    [key: string]: {
      message: string;
    };
  };
};
export const FormRenderer: FC<FormRendererProps> = ({
  fields,
  parentFieldName = "",
  errors = {},
}) => {
  const form = useFormContext();
  const {
    formState: { errors: formErrors },
  } = form;
  const flatFormErrors = flatten(formErrors);
  return (
    <>
      {fields.map((rowFields, rowIndex) => {
        return (
          <Row key={rowIndex + "-" + rowFields.length}>
            {rowFields.map((field, fieldIndex) => {
              const { renderAsFormItem = true } = field;
              const Component = field.component || React.Fragment;
              const name = [parentFieldName, field.name]
                .filter((key) => !!key)
                .join(".");

              const componentProps =
                typeof field.props === "function"
                  ? field.props(form, parentFieldName)
                  : field.props;
              return (
                <Col
                  key={field.name + "-" + fieldIndex}
                  className={`col-lg-${12 / rowFields.length}`}
                >
                  {renderAsFormItem ? (
                    field.component && (
                      <Controller
                        control={form.control}
                        name={name}
                        render={({ field: formField }) => {
                          return (
                            <Suspense fallback="Loading...">
                              <Component
                                {...componentProps}
                                {...formField}
                                error={
                                  (errors[field.name] &&
                                    errors[field.name].message) ||
                                  (flatFormErrors[name + ".message"] &&
                                    flatFormErrors[name + ".message"])
                                }
                                value={
                                  typeof componentProps.value !== "undefined"
                                    ? componentProps.value
                                    : typeof formField.value === "undefined" ||
                                      formField.value === null
                                    ? ""
                                    : formField.value
                                }
                                onChange={(v: any) => {
                                  // handle custom transformer. e.g. for number inputs
                                  let value = v?.target?.value || v;
                                  if (field.valueTransformer) {
                                    value = field.valueTransformer(value);
                                  }
                                  formField.onChange(value);
                                }}
                              />
                            </Suspense>
                          );
                        }}
                      />
                    )
                  ) : (
                    <Suspense fallback="Loading...">
                      <Component {...componentProps} parentFieldName={name} />
                    </Suspense>
                  )}
                </Col>
              );
            })}
          </Row>
        );
      })}
    </>
  );
};
